import { __assign, __rest } from "tslib";
import * as React from 'react';
import clsx from 'clsx';
import { makeStyles, createStyles } from '../styles';
import { TextField } from '../TextField';
import { ClickAwayListener } from '../ClickAwayListener';
import { Popper } from '../Popper';
import { Paper } from '../Paper';
import { InputAdornment } from '../InputAdornment';
import { IconButton } from '../IconButton';
import { TriangleDown } from '../../internal/icons/small/TriangleDown';
import { Clear } from '../../internal/icons/small/Clear';
import { ComboboxContextProvider, useComboboxContext } from './Combobox.context';
import { defaultComponents } from './Combobox.components';
export var useStyles = makeStyles(function (theme) {
    return createStyles({
        container: {
            position: 'relative',
            display: function (_a) {
                var fullWidth = _a.fullWidth;
                return fullWidth ? 'block' : 'inline-block';
            },
        },
        textField: {
            minWidth: function (_a) {
                var minWidth = _a.minWidth;
                return minWidth !== null && minWidth !== void 0 ? minWidth : '216px';
            },
        },
        inputWrap: {
            // Positioning context for combobox input
            position: 'relative',
            display: 'flex',
            minHeight: '38px',
        },
        inputAdornment: {
            marginLeft: 'auto',
        },
        inputArrowIcon: {
            color: function (_a) {
                var disabled = _a.disabled;
                return disabled ? theme.palette.text.disabled : theme.palette.text.primary;
            },
        },
        clearButton: {
            // Inset focus outline
            '&::before': {
                // !important needed to override specificity from focusOutline
                top: '4px !important',
                left: '4px !important',
                right: '4px !important',
                bottom: '4px !important',
            },
        },
        notSearchable: {
            cursor: 'pointer',
        },
        popper: {
            zIndex: theme.zIndex.modal,
            // Match component width (100%) + border width (2px)
            width: 'calc(100% + 2px)',
        },
    });
});
export var Combobox = function () {
    var _a;
    var _b = useComboboxContext(), comboboxProps = _b.comboboxProps, components = _b.components, containerRef = _b.containerRef, inputRef = _b.inputRef, menuIsOpen = _b.menuIsOpen, setMenuIsOpen = _b.setMenuIsOpen, handleOnClick = _b.handleOnClick, handleOnBlur = _b.handleOnBlur, handleOnFocus = _b.handleOnFocus, handleOnKeyDown = _b.handleOnKeyDown, hasValue = _b.hasValue, clearValue = _b.clearValue, setHasFocusWithin = _b.setHasFocusWithin, offScreenAnnouncement = _b.offScreenAnnouncement, hasFocusWithin = _b.hasFocusWithin;
    var id = comboboxProps.id, disabled = comboboxProps.disabled, options = comboboxProps.options, value = comboboxProps.value, inputValue = comboboxProps.inputValue, onChange = comboboxProps.onChange, onKeyDown = comboboxProps.onKeyDown, onBlur = comboboxProps.onBlur, onClick = comboboxProps.onClick, onFocus = comboboxProps.onFocus, onSearch = comboboxProps.onSearch, searchFilter = comboboxProps.searchFilter, defaultValue = comboboxProps.defaultValue, floatingLabel = comboboxProps.floatingLabel, InputProps = comboboxProps.InputProps, InputLabelProps = comboboxProps.InputLabelProps, isMulti = comboboxProps.isMulti, isClearable = comboboxProps.isClearable, isSearchable = comboboxProps.isSearchable, closeOnSelect = comboboxProps.closeOnSelect, showSelectedOptions = comboboxProps.showSelectedOptions, strings = comboboxProps.strings, fullWidth = comboboxProps.fullWidth, isLoading = comboboxProps.isLoading, className = comboboxProps.className, containerProps = comboboxProps.containerProps, ListboxProps = comboboxProps.ListboxProps, SearchProps = comboboxProps.SearchProps, minWidth = comboboxProps.minWidth, other = __rest(comboboxProps, ["id", "disabled", "options", "value", "inputValue", "onChange", "onKeyDown", "onBlur", "onClick", "onFocus", "onSearch", "searchFilter", "defaultValue", "floatingLabel", "InputProps", "InputLabelProps", "isMulti", "isClearable", "isSearchable", "closeOnSelect", "showSelectedOptions", "strings", "fullWidth", "isLoading", "className", "containerProps", "ListboxProps", "SearchProps", "minWidth"]);
    var InputWrap = components.InputWrap, Listbox = components.Listbox, OffScreenAnnouncement = components.OffScreenAnnouncement, Search = components.Search;
    var clearButtonLabel = strings.clearButtonLabel;
    var classes = useStyles({ disabled: disabled, fullWidth: fullWidth, minWidth: minWidth });
    var shrinkLabel = hasValue || (floatingLabel && hasFocusWithin);
    return (React.createElement(React.Fragment, null,
        React.createElement(ClickAwayListener, { onClickAway: function () { return setMenuIsOpen(false); } },
            React.createElement("div", __assign({ onFocus: function () { return setHasFocusWithin(true); }, onBlur: function () { return setHasFocusWithin(false); } }, containerProps, { 
                // Declare after spread to merge consumer styles with internal styles.
                className: clsx(classes.container, containerProps === null || containerProps === void 0 ? void 0 : containerProps.className) }),
                React.createElement(TextField, __assign({ id: id, inputRef: inputRef, className: clsx(classes.textField, className), onKeyDown: handleOnKeyDown, onClick: handleOnClick, onFocus: handleOnFocus, onBlur: handleOnBlur, InputProps: __assign({ ref: containerRef, className: clsx(classes.inputWrap, (_a = {}, _a[classes.notSearchable] = !isSearchable, _a)), inputComponent: InputWrap, endAdornment: (React.createElement(InputAdornment, { className: classes.inputAdornment, position: "end" },
                            isClearable && !disabled && hasValue && (React.createElement(IconButton, { "aria-label": clearButtonLabel, onClick: clearValue, className: classes.clearButton },
                                React.createElement(Clear, null))),
                            React.createElement(TriangleDown, { className: classes.inputArrowIcon }))), readOnly: true }, InputProps), InputLabelProps: __assign({ htmlFor: id, id: "".concat(id, "-label"), shrink: shrinkLabel }, InputLabelProps), floatingLabel: floatingLabel, disabled: disabled, fullWidth: fullWidth }, other)),
                React.createElement(Popper, { open: !disabled && menuIsOpen, anchorEl: containerRef.current, container: containerRef.current, placement: "bottom-start", role: undefined, popperOptions: { scheduleUpdate: function () { return true; } }, className: classes.popper, modifiers: {
                        flip: {
                            enabled: true,
                        },
                        preventOverflow: {
                            enabled: true,
                            boundariesElement: 'scrollParent',
                        },
                        offset: {
                            offset: '0, 5px',
                        },
                    } },
                    React.createElement(Paper, { variant: "dropdown" },
                        isSearchable && React.createElement(Search, null),
                        React.createElement(Listbox, null))))),
        React.createElement(OffScreenAnnouncement, { type: "assertive", message: offScreenAnnouncement })));
};
var ComboboxWithContext = function (props) {
    var components = props.components, other = __rest(props, ["components"]);
    return (React.createElement(ComboboxContextProvider, __assign({ components: defaultComponents(components) }, other),
        React.createElement(Combobox, __assign({}, other))));
};
export default ComboboxWithContext;
//# sourceMappingURL=Combobox.js.map